import { PencilSimpleLine } from "@phosphor-icons/react";
import { useState } from "react";
import { Link } from "react-router-dom";

import { ManageSubscriptionDialog } from "features/subscriptions/components";

import ManageAgencySeatsDialog from "common/components/ManageAgencySeatsDialog";
import RenderIf from "common/components/RenderIf";
import RestrictedComponent from "common/components/RestrictedComponent";
import { Button } from "common/components/ui/Button";
import { Tag } from "common/components/ui/Tag";
import { useSelectedWorkspaceContext } from "common/context/SelectedWorkspaceContext";
import usePromotions from "common/datahooks/usePromotions";
import useLicensedAccounts from "common/hooks/useLicensedAccounts";
import useTwBreakpoint from "common/hooks/useTwBreakpoint";

import NonRenewingTag from "../../workspace-settings/components/Billing/SubscriptionCards/NonRenewingTag";
import ScheduledChangeTag from "../../workspace-settings/components/Billing/SubscriptionCards/Rented/ScheduledChangeTag";
import SubscriptionInfo from "../../workspace-settings/components/Billing/SubscriptionCards/SubscriptionInfo";
import CustomerSubRenewalPrice from "./CustomerSubRenewalPrice";
import SubscriptionCardSeatsSummary from "./SubscriptionCardSeatsSummary";
import SubscriptionDetailsCard from "./SubscriptionDetailsCard";

export default function AgencySubscriptionCard({
  isAgencyPage,
}: {
  isAgencyPage: boolean;
}) {
  const isDesktop = useTwBreakpoint("lg");
  const [isOpen, setIsOpen] = useState(false);
  const {
    id: workspaceId,
    agency_license_count,
    role,
  } = useSelectedWorkspaceContext();
  const { agencyLicensedAccounts } = useLicensedAccounts(workspaceId);
  const { agencyPlan, isLoadingPromotions } = usePromotions();

  const { subscription } = agencyPlan;

  const {
    has_changes: hasChanges,
    status,
    item_price: itemPrice,
  } = subscription;

  let tagComponent = <Tag variant="disabled">Inactive</Tag>;
  let priceComponent = (
    <RenderIf condition={role === "owner"}>
      <Button
        asChild
        size={isDesktop ? "sm" : "md"}
        intent="iconOnly"
        variant="outline-black"
      >
        <Link to="../agency">
          <PencilSimpleLine />
        </Link>
      </Button>
    </RenderIf>
  );

  if (status === "non_renewing") {
    tagComponent = <NonRenewingTag />;
  } else if (hasChanges) {
    tagComponent = <ScheduledChangeTag />;
  } else if (status === "active") {
    tagComponent =
      itemPrice.period === 6 &&
      itemPrice.id === "Agency-Plan-USD-Every-6-months" ? (
        <Tag variant="gradient">6 months deal</Tag>
      ) : (
        <Tag variant="success">Active</Tag>
      );
  }

  if (isAgencyPage && agencyPlan.subscription.status === "active") {
    priceComponent = (
      <CustomerSubRenewalPrice
        pricingPeriodInfo={agencyPlan.subscription.item_price}
      />
    );
  }

  const mainComponent = subscription &&
    subscription.status !== "cancelled" &&
    !isAgencyPage && (
      <SubscriptionCardSeatsSummary
        accounts={agencyLicensedAccounts}
        availableSeatsCount={agency_license_count}
        extraTagComponent={
          <Tag variant="tertiary" size="sm">
            {agencyPlan.unassigned_license_count} available
          </Tag>
        }
      />
    );

  let footerComponent = isAgencyPage ? (
    <SubscriptionInfo
      isLoading={isLoadingPromotions}
      subscription={isAgencyPage && subscription}
      buttonComponent={
        <Button onClick={() => setIsOpen(true)} size={isDesktop ? "sm" : "md"}>
          Manage subscription
        </Button>
      }
    />
  ) : (
    <RestrictedComponent visibleForPermissions={["billing_full"]}>
      <Button
        size={isDesktop ? "sm" : "md"}
        className="ml-auto max-lg:mt-9"
        onClick={() => setIsOpen(true)}
      >
        Manage workspace seats
      </Button>
    </RestrictedComponent>
  );

  if (subscription?.status === "cancelled") {
    footerComponent = (
      <Button asChild size={isDesktop ? "sm" : "md"} className="ml-auto">
        <Link to="../agency">Reactivate subscription</Link>
      </Button>
    );
  }

  return (
    <>
      <SubscriptionDetailsCard
        plan="agency"
        footerComponent={footerComponent}
        priceComponent={priceComponent}
        tagComponent={tagComponent}
        mainComponent={mainComponent}
      />
      {isAgencyPage ? (
        <ManageSubscriptionDialog
          plan="agency"
          isOpen={isOpen}
          setIsOpen={setIsOpen}
        />
      ) : (
        <ManageAgencySeatsDialog isOpen={isOpen} setIsOpen={setIsOpen} />
      )}
    </>
  );
}
